import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useMatches } from '@remix-run/react';
import type { ProfessionalBannersData } from '@modules/professional/page/OverviewPage';

interface CurrentTabBannerResult {
    imageUrl: string | null;
    bannerId: number | null;
    wasUpscaled: boolean;
}

/**
 * Returns the matched banner object (image URL + LibraryImage ID)
 * for the current professional detail tab.
 */
export const useCurrentTabBanner = (
    banners?: ProfessionalBannersData,
    squareBanner?: boolean,
): CurrentTabBannerResult => {
    const { mobile: isMobile } = useAppDisplayModeContext();
    const matches = useMatches();
    const currentRoute = matches[matches.length - 1]!.pathname;
    const currentTabPath =
        currentRoute
            .split('/')
            .filter((path) => path)
            .pop() ?? '';

    if (!banners || banners.length === 0) {
        return { imageUrl: null, bannerId: null, wasUpscaled: false };
    }

    const isAboutSegment = currentTabPath === 'info';

    let banner;
    if (isAboutSegment) {
        banner = banners.find((b) => b.bannerSegment === 'ABOUTUS');
    }
    if (!banner) {
        banner = banners.find((b) => !b.bannerSegment);
    }
    if (!banner) {
        banner = banners[0] ?? null;
    }
    if (!banner) {
        return { imageUrl: null, bannerId: null, wasUpscaled: false };
    }

    const responsiveImage = squareBanner
        ? banner?.libraryImageFile?.DetailPageSegmentBanner
        : banner?.libraryImageFile?.DetailBannerStandard;

    const imageUrl =
        (isMobile ? responsiveImage?.Mobile : responsiveImage?.Desktop) ?? null;

    return { imageUrl, bannerId: banner.id, wasUpscaled: !!banner.wasUpscaled };
};

/** Convenience wrapper that returns only the image URL. */
export const useCurrentTabBannerImage = (
    banners?: ProfessionalBannersData,
    squareBanner?: boolean,
): string | null => {
    return useCurrentTabBanner(banners, squareBanner).imageUrl;
};
